<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Consultation Management' : 'Deleted Consultations'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all consultations' : 'Restore or permanently delete consultations'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/consultations/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Consultation
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Consultations</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Consultations"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Title, notes..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statusItems"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-check-circle"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Doctor Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.doctor_id"
                    :items="doctors"
                    item-text="text"
                    item-value="value"
                    label="Doctor"
                    prepend-inner-icon="mdi-doctor"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Patient Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.patient_id"
                    :items="patients"
                    item-text="text"
                    item-value="value"
                    label="Patient"
                    prepend-inner-icon="mdi-account-heart"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <!-- Consultation Type Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.type_id"
                    :items="consultationTypes"
                    item-text="text"
                    item-value="value"
                    label="Consultation Type"
                    prepend-inner-icon="mdi-medical-bag"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Consultations Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Consultations List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-medical-bag</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Consultation" : "Consultations" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Buttons -->
                    <div v-if="!isDeletePage() && consultations.length > 0" class="d-flex align-center">
                      <!-- Regular Export Button -->
                      <v-btn
                        x-small
                        color="success"
                        outlined
                        @click="exportToExcel"
                        class="px-2 mr-2"
                        :loading="isExporting"
                      >
                        <v-icon left x-small>mdi-microsoft-excel</v-icon>
                        Export
                      </v-btn>
                      
                      <!-- Doctor Excel Export Button -->
                      <v-btn
                        x-small
                        color="primary"
                        outlined
                        @click="openDateFilterDialog"
                        class="px-2"
                        :loading="isDoctorExporting"
                      >
                        <v-icon left x-small>mdi-account-cash</v-icon>
                        Doctor Excel
                      </v-btn>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading consultations..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="computedHeaders"
                :items="consultations"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.consultation_id`]="{ item }">
                  <span class="font-weight-medium text-caption primary--text">
                    CON-{{ item.id }}
                  </span>
                </template>

                <!-- Status Column -->
                <template #[`item.status`]="{ item }">
                  <v-chip
                    :color="consultationStatusColor(item.status)"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{ consultationStatusIcon(item.status) }}
                    </v-icon>
                    {{ consultationStatusText(item.status) }}
                  </v-chip>
                </template>

                <!-- Title Column -->
                <template #[`item.title`]="{ item }">
                  <span class="font-weight-medium">
                    {{ item.title || "No Title" }}
                  </span>
                </template>

                <!-- Doctor Column -->
                <template #[`item.doctor`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="blue">
                      <v-icon color="white" size="16">mdi-doctor</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2">
                        {{ item.doctor ? item.doctor.user_name : "-" }}
                      </div>
                      <div
                        class="text-caption grey--text"
                        v-if="item.doctor && item.doctor.position"
                      >
                        {{ item.doctor.position.name }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Patient Column -->
                <template #[`item.patient`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="green">
                      <v-icon color="white" size="16">mdi-account-heart</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2">
                        <router-link
                          v-if="item.patient && item.patient.id"
                          :to="`/patient/${encryptIt(item.patient.id)}`"
                          class="text-decoration-none primary--text font-weight-medium"
                        >
                          {{ item.patient.user_name }}
                        </router-link>
                        <span v-else>-</span>
                      </div>
                      <div class="text-caption grey--text" v-if="item.patient && item.patient.phone">
                        {{ item.patient.phone }}
                      </div>
                      <div class="text-caption grey--text" v-else-if="item.patient && item.patient.guest_code">
                        Guest: {{ item.patient.guest_code }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Session Type Column -->
                <template #[`item.session_type`]="{ item }">
                  <status-chip :color="item.appointment_id ? 'purple' : 'teal'" :icon="item.appointment_id ? 'mdi-calendar-check' : 'mdi-lightning-bolt'" :label="String(item.appointment_id ? 'Scheduled' : 'Realtime')" />
                </template>

                <!-- Category Column (doctor's pathy) -->
                <template #[`item.category`]="{ item }">
                  <span class="text-caption">
                    {{ item.doctor && item.doctor.pathy ? item.doctor.pathy.description : '-' }}
                  </span>
                </template>

                <!-- Start Time Column -->
                <template #[`item.start_time`]="{ item }">
                  <div class="text-caption">
                    {{ formatDateTime(item.start_time) }}
                  </div>
                </template>

                <!-- Duration Column -->
                <template #[`item.duration`]="{ item }">
                  <div class="text-caption font-weight-medium">
                    {{ consultationDuration(item) }}
                  </div>
                </template>

                <!-- Rate per Minute Column -->
                <template #[`item.rate_per_min`]="{ item }">
                  <div class="text-caption">
                    {{ consultationRatePerMin(item) }}
                  </div>
                </template>

                <!-- Payment Column -->
                <template #[`item.payment`]="{ item }">
                  <div class="text-caption font-weight-medium">
                    {{ consultationPayment(item) }}
                  </div>
                </template>


                <!-- Doctor Notes Column (admin only) -->
                <template #[`item.doctor_notes`]="{ item }">
                  <div class="text-caption">
                    {{ truncateText(item.doctor_notes, 40) || '-' }}
                  </div>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Download Invoice Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="downloadInvoice(item)"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                            :loading="item.downloading"
                            :disabled="item.downloading"
                          >
                            <v-icon x-small>mdi-download</v-icon>
                          </v-btn>
                        </template>
                        <span>Download Patient Invoice</span>
                      </v-tooltip>

                      <!-- Download Doctor Invoice Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            @click="downloadDoctorInvoice(item)"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                            :loading="item.downloadingDoctor"
                            :disabled="item.downloadingDoctor"
                          >
                            <v-icon x-small>mdi-account-cash</v-icon>
                          </v-btn>
                        </template>
                        <span>Download Doctor Commission Invoice</span>
                      </v-tooltip>

                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/consultations/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Consultation</span>
                      </v-tooltip>

                      <!-- View Prescription Button -->
                      <v-tooltip bottom v-if="item.prescription">
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="teal"
                            dark
                            :to="`/prescriptions/${encryptIt(item.prescription.id)}/details`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-prescription</v-icon>
                          </v-btn>
                        </template>
                        <span>View Prescription</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Consultation</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="restoreConsultation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Consultation</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-medical-bag</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Consultations Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No consultations match your current filters. Try adjusting your search criteria."
                          : "You haven't added any consultations yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/consultations/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Consultation
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_consultation" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Consultation deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_consultation = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_consultation" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Consultation restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_consultation = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Date Filter Dialog -->
      <v-dialog v-model="showDateFilterDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline primary white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-calendar-range</v-icon>
            Export Doctor Excel
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center mb-4">
              <v-icon size="64" color="primary" class="mb-3">mdi-microsoft-excel</v-icon>
              <h3 class="mb-3">Select Date Range for Export</h3>
              <p class="text-body-2 grey--text mb-2">
                Choose the date range to filter consultations for the doctor commission report.
              </p>
              <p class="text-caption orange--text">
                <v-icon small color="orange" class="mr-1">mdi-information</v-icon>
                Both dates are required for filtering, or leave both empty for all consultations.
              </p>
            </div>

            <!-- Doctor Selection -->
            <v-row class="mb-3">
              <v-col cols="12">
                <v-autocomplete
                  v-model="dateFilter.doctorId"
                  :items="consultationDoctors"
                  item-text="name"
                  item-value="id"
                  label="Select Doctor (Optional)"
                  prepend-icon="mdi-doctor"
                  outlined
                  dense
                  clearable
                  no-data-text="Loading doctors..."
                  :loading="isLoadingDoctors"
                  placeholder="All doctors"
                >
                  <template #item="{ item }">
                    <v-list-item-avatar>
                      <v-avatar size="32" color="primary">
                        <v-icon color="white" small>mdi-doctor</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle>{{ item.consultationCount }} consultations</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template #selection="{ item }">
                    <span>{{ item.name }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <!-- Date Selection -->
            <v-row>
              <v-col cols="12" md="6">
                <v-menu
                  v-model="fromDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFilter.fromDate"
                      label="From Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      @click:clear="dateFilter.fromDate = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dateFilter.fromDate"
                    @input="fromDateMenu = false"
                    :max="dateFilter.toDate || new Date().toISOString().substr(0, 10)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-menu
                  v-model="toDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFilter.toDate"
                      label="To Date"
                      prepend-icon="mdi-calendar"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                      clearable
                      @click:clear="dateFilter.toDate = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dateFilter.toDate"
                    @input="toDateMenu = false"
                    :min="dateFilter.fromDate"
                    :max="new Date().toISOString().substr(0, 10)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-row class="mt-2">
              <v-col cols="12">
                <v-card outlined class="pa-3" color="blue-grey lighten-5">
                  <div class="d-flex align-center">
                    <v-icon color="info" class="mr-2">mdi-information</v-icon>
                    <div>
                      <div class="text-caption font-weight-bold">Export Information</div>
                      <div class="text-caption grey--text">
                        {{ getExportSummary() }}
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDateFilter"
              :disabled="isDoctorExporting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              @click="exportDoctorExcelWithDates"
              :loading="isDoctorExporting"
              :disabled="isDoctorExporting || !canExportDates"
            >
              <v-icon left>mdi-microsoft-excel</v-icon>
              Export Excel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this consultation?
              </h3>

              <div
                class="consultation-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-medical-bag</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        consultationToDelete ? consultationToDelete.title : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        consultationToDelete
                          ? formatDateTime(consultationToDelete.start_time)
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the consultation
                to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted consultations section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Consultation
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";

export default {
  name: "ConsultationsIndex",
  data() {
    return {
      filter: {
        search_keyword: "",
        status: "",
        is_active: "",
        doctor_id: "",
        patient_id: "",
        type_id: "",
        show_deleted: false,
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      cardHeight: 600,
      showDeleteDialog: false,
      consultationToDelete: null,
      isDeleting: false,
      consultations: [],
      isLoading: false,
      isExporting: false,
      isDoctorExporting: false,
      is_deleted_consultation: false,
      is_restored_consultation: false,
      showDateFilterDialog: false,
      fromDateMenu: false,
      toDateMenu: false,
      isLoadingDoctors: false,
      dateFilter: {
        fromDate: null,
        toDate: null,
        doctorId: null,
      },
      consultationDoctors: [],
      doctors: [
        {
          id: 0,
          text: "Select Doctor",
          value: "",
        },
      ],
      patients: [
        {
          id: 0,
          text: "Select Patient",
          value: "",
        },
      ],
      statusItems: [
        { text: "All Statuses", value: "" },
        { text: "Scheduled", value: "0" },
        { text: "In Progress", value: "1" },
        { text: "Completed", value: "2" },
        { text: "Cancelled", value: "3" },
        { text: "Missed", value: "4" },
      ],
      activeItems: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      consultationTypes: [
        { value: "", text: "All Types" },
        { value: 1, text: "General Consultation" },
        { value: 2, text: "Follow-up" },
        { value: 3, text: "Specialist Consultation" },
        { value: 4, text: "Emergency Consultation" },
        { value: 5, text: "Routine Check-up" },
      ],
      headers: [
        { text: "ID", value: "consultation_id", sortable: false, width: "80px", align: "center" },
        { text: "Status", value: "status", sortable: false, width: "130px" },
        { text: "Type", value: "session_type", sortable: false, width: "110px" },
        { text: "Doctor", value: "doctor", sortable: false, width: "160px" },
        { text: "Patient", value: "patient", sortable: false, width: "150px" },
        { text: "Category", value: "category", sortable: false, width: "120px" },
        { text: "Date", value: "start_time", sortable: false, width: "130px" },
        { text: "Duration", value: "duration", sortable: false, width: "90px" },
        { text: "Rate/Min", value: "rate_per_min", sortable: false, width: "90px" },
        { text: "Payment", value: "payment", sortable: false, width: "100px" },
        { text: "Doctor Notes", value: "doctor_notes", sortable: false, width: "150px" },
        { text: "Created", value: "created_at", sortable: false, width: "110px" },
        { text: "Actions", value: "actions", sortable: false, width: "130px", align: "center" },
      ],
    };
  },

  computed: {
    ...mapGetters("auth", ["isSuperAdmin", "isAdmin"]),
    isAdminOrSuperAdmin() {
      return this.isSuperAdmin || this.isAdmin;
    },
    computedHeaders() {
      return this.headers.filter(h => h.value !== 'doctor_notes' || this.isAdminOrSuperAdmin);
    },
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.status ||
        this.filter.is_active ||
        this.filter.doctor_id ||
        this.filter.patient_id ||
        this.filter.type_id
      );
    },
    canExportDates() {
      // Both dates are required for the backend to filter, or no dates for all consultations
      return (!this.dateFilter.fromDate && !this.dateFilter.toDate) || 
             (this.dateFilter.fromDate && this.dateFilter.toDate);
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.getData();
    this.getUsers();
    this.calculateCardHeight();
    window.addEventListener("resize", this.calculateCardHeight);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.calculateCardHeight);
  },

  methods: {
    async getUsers() {
      await axios
        .get(`users`)
        .then((response) => {
          const users = response.data.data;

          // Reset arrays
          this.doctors = [{ id: 0, text: "Select Doctor", value: "" }];
          this.patients = [{ id: 0, text: "Select Patient", value: "" }];

          // Filter users by position
          const doctorUsers = users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              (user.position.name.toLowerCase().includes("doctor") ||
                user.position.name.toLowerCase().includes("physician") ||
                user.position.name.toLowerCase().includes("specialist") ||
                user.position.name.toLowerCase().includes("consultant") ||
                user.position.name.toLowerCase().includes("therapist") ||
                user.position.name.toLowerCase().includes("practitioner"))
          );

          const patientUsers = users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              (user.position.name.toLowerCase().includes("patient") ||
                user.position.name.toLowerCase().includes("client") ||
                user.position.name.toLowerCase().includes("member") ||
                user.position.name.toLowerCase().includes("customer") ||
                user.position.name.toLowerCase().includes("user"))
          );

          // Populate dropdowns
          doctorUsers.forEach((user) => {
            this.doctors.push({
              id: user.id,
              text: user.position
                ? `${user.user_name} (${user.position.name})`
                : user.user_name,
              value: user.id,
            });
          });

          patientUsers.forEach((user) => {
            this.patients.push({
              id: user.id,
              text: user.position
                ? `${user.user_name} (${user.position.name})`
                : user.user_name,
              value: user.id,
            });
          });

          // Fallback if no users found
          if (this.doctors.length === 1 && this.patients.length === 1) {
            users.forEach((user) => {
              this.doctors.push({
                id: user.id,
                text: user.user_name,
                value: user.id,
              });
              this.patients.push({
                id: user.id,
                text: user.user_name,
                value: user.id,
              });
            });
          }
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async getData() {
      console.log("Get Consultations Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.status = this.filter.status || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.doctor_id = this.filter.doctor_id || "";
      this.filter.patient_id = this.filter.patient_id || "";
      this.filter.type_id = this.filter.type_id || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&status=" +
        this.filter.status +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&type_id=" +
        this.filter.type_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      await axios
        .get(`consultations?${query}`)
        .then((response) => {
          this.consultations = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    showDeleteConfirmation(consultation) {
      this.consultationToDelete = consultation;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.consultationToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.consultationToDelete) return;

      this.isDeleting = true;

      await axios
        .post(
          `/consultations/delete/${this.encryptIt(
            this.consultationToDelete.id
          )}`
        )
        .then(() => {
          this.showDeleteDialog = false;
          this.consultationToDelete = null;
          this.isDeleting = false;
          this.is_deleted_consultation = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.isDeleting = false;
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async deleteConsultation(consultation) {
      await axios
        .post(`/consultations/delete/${this.encryptIt(consultation.id)}`)
        .then(() => {
          this.is_deleted_consultation = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async restoreConsultation(consultation) {
      await axios
        .post(`/consultations/restore/${this.encryptIt(consultation.id)}`)
        .then(() => {
          this.is_restored_consultation = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async exportToExcel() {
      this.isExporting = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&status=" +
        this.filter.status +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&type_id=" +
        this.filter.type_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      try {
        const response = await axios.get(`consultations/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `consultations_${new Date().toISOString().split("T")[0]}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }

      this.isExporting = false;
    },

    openDateFilterDialog() {
      this.showDateFilterDialog = true;
      this.loadConsultationDoctors();
    },

    async loadConsultationDoctors() {
      this.isLoadingDoctors = true;
      try {
        const response = await axios.get('consultations/doctors_with_consultations');
        if (response.data.success) {
          this.consultationDoctors = [
            { id: null, name: 'All Doctors', consultationCount: 'All' },
            ...response.data.data.map(doctor => ({
              id: doctor.id,
              name: `Dr. ${doctor.first_name || ''} ${doctor.last_name || ''}`.trim() || doctor.user_name,
              consultationCount: doctor.consultations_count || 0
            }))
          ];
        }
      } catch (error) {
        console.error('Error loading consultation doctors:', error);
        this.consultationDoctors = [
          { id: null, name: 'All Doctors', consultationCount: 'All' }
        ];
      } finally {
        this.isLoadingDoctors = false;
      }
    },

    cancelDateFilter() {
      this.showDateFilterDialog = false;
      this.dateFilter.fromDate = null;
      this.dateFilter.toDate = null;
      this.dateFilter.doctorId = null;
    },

    getExportSummary() {
      let summary = '';
      
      // Doctor filter summary
      const selectedDoctor = this.consultationDoctors.find(d => d.id === this.dateFilter.doctorId);
      if (selectedDoctor && selectedDoctor.id !== null) {
        summary += `Doctor: ${selectedDoctor.name}. `;
      } else {
        summary += 'All doctors. ';
      }
      
      // Date filter summary
      if (!this.dateFilter.fromDate && !this.dateFilter.toDate) {
        summary += "All consultations will be exported with current filters applied.";
      } else if (this.dateFilter.fromDate && this.dateFilter.toDate) {
        summary += `Consultations from ${this.formatDate(this.dateFilter.fromDate)} to ${this.formatDate(this.dateFilter.toDate)} will be exported.`;
      } else if (this.dateFilter.fromDate || this.dateFilter.toDate) {
        summary += "Please select both From Date and To Date for date filtering to work.";
      } else {
        summary += "All consultations will be exported.";
      }
      
      return summary;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    },

    async exportDoctorExcelWithDates() {
      this.isDoctorExporting = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&status=" +
        this.filter.status +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&type_id=" +
        this.filter.type_id +
        "&search_keyword=" +
        this.filter.search_keyword;

      // Add date filters if provided (backend requires both dates)
      if (this.dateFilter.fromDate && this.dateFilter.toDate) {
        query += "&start_date=" + this.dateFilter.fromDate;
        query += "&end_date=" + this.dateFilter.toDate;
      }
      
      // Add doctor filter if selected
      if (this.dateFilter.doctorId) {
        query += "&doctor_id=" + this.dateFilter.doctorId;
      }

      try {
        const response = await axios.get(`consultations/doctor_excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        
        // Generate filename with filters specified
        let filename = "doctor_consultations_";
        
        // Add doctor to filename if selected
        if (this.dateFilter.doctorId) {
          const selectedDoctor = this.consultationDoctors.find(d => d.id === this.dateFilter.doctorId);
          if (selectedDoctor) {
            const doctorName = selectedDoctor.name.replace(/[^a-zA-Z0-9]/g, '_');
            filename += `${doctorName}_`;
          }
        }
        
        // Add date range to filename if specified
        if (this.dateFilter.fromDate && this.dateFilter.toDate) {
          filename += `${this.dateFilter.fromDate}_to_${this.dateFilter.toDate}`;
        } else if (this.dateFilter.fromDate) {
          filename += `from_${this.dateFilter.fromDate}`;
        } else if (this.dateFilter.toDate) {
          filename += `until_${this.dateFilter.toDate}`;
        } else {
          filename += new Date().toISOString().split("T")[0];
        }
        filename += ".xlsx";
        
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Close dialog and reset filters
        this.showDateFilterDialog = false;
        this.dateFilter.fromDate = null;
        this.dateFilter.toDate = null;
        this.dateFilter.doctorId = null;

        // Show success message
        this.$store.dispatch("errors/setSuccess", {
          message: "Doctor consultation report downloaded successfully!",
        });
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          this.$store.dispatch("errors/setError", {
            message: "Authentication required. Please login again.",
          });
          window.location.href = "/auth/login";
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error downloading doctor consultation report. Please try again.",
          });
        }
      }

      this.isDoctorExporting = false;
    },

    async exportDoctorExcel() {
      // This method is kept for backward compatibility but now just calls the dialog
      this.openDateFilterDialog();
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    consultationStatusText(status) {
      const map = { 0: "Scheduled", 1: "In Progress", 2: "Completed", 3: "Cancelled", 4: "Missed" };
      return map[status] ?? "Unknown";
    },
    consultationStatusColor(status) {
      const map = { 0: "blue", 1: "orange", 2: "success", 3: "error", 4: "grey" };
      return map[status] ?? "grey";
    },
    consultationStatusIcon(status) {
      const map = { 0: "mdi-calendar-clock", 1: "mdi-video", 2: "mdi-check-circle", 3: "mdi-close-circle", 4: "mdi-account-off" };
      return map[status] ?? "mdi-help-circle";
    },

    consultationDuration(item) {
      if (!item.start_time || !item.end_time) return '-';
      const minutes = Math.round((new Date(item.end_time) - new Date(item.start_time)) / 60000);
      if (minutes < 1) return '< 1 min';
      if (minutes < 60) return `${minutes} min`;
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },

    consultationRatePerMin(item) {
      if (item.consultation_billings && item.consultation_billings.length > 0) {
        const rate = item.consultation_billings[0].rate_per_minute;
        if (rate) return `₹${parseFloat(rate).toFixed(0)}/min`;
      }
      if (item.doctor && item.doctor.consultation_rate_per_minute) {
        return `₹${parseFloat(item.doctor.consultation_rate_per_minute).toFixed(0)}/min`;
      }
      return '-';
    },

    consultationPayment(item) {
      if (item.consultation_billings && item.consultation_billings.length > 0) {
        const total = item.consultation_billings[0].total_amount_deducted;
        if (total > 0) return `₹${total}`;
      }
      if (item.appointment && item.appointment.amount_paid) {
        return `₹${parseFloat(item.appointment.amount_paid).toFixed(0)}`;
      }
      return '-';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "N/A";
      try {
        return new Date(dateTime).toLocaleString();
      } catch {
        return "Invalid Date";
      }
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.status = "";
      this.filter.is_active = "";
      this.filter.doctor_id = "";
      this.filter.patient_id = "";
      this.filter.type_id = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.status) count++;
      if (this.filter.is_active) count++;
      if (this.filter.doctor_id) count++;
      if (this.filter.patient_id) count++;
      if (this.filter.type_id) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    async downloadInvoice(consultation) {
      // Set downloading state for this specific consultation
      this.$set(consultation, "downloading", true);

      try {
        const response = await axios.get(
          `/consultations/${this.encryptIt(consultation.id)}/download-invoice`,
          {
            responseType: "blob",
          }
        );

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Generate filename with patient name and date
        const patientName = consultation.patient
          ? consultation.patient.user_name.replace(/\s+/g, "_")
          : "Unknown";
        const date = new Date().toISOString().split("T")[0];
        const filename = `consultation_invoice_${patientName}_${date}.pdf`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Show success message
        this.$store.dispatch("errors/setSuccess", {
          message: "Patient invoice downloaded successfully!",
        });
      } catch (error) {
        console.error("Error downloading invoice:", error);

        if (error.response && error.response.status === 401) {
          this.$store.dispatch("errors/setError", {
            message: "Authentication required. Please login again.",
          });
          this.$router.push("/login");
        } else if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message: "Invoice not found for this consultation.",
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Error downloading invoice. Please try again.",
          });
        }
      } finally {
        // Remove downloading state
        this.$set(consultation, "downloading", false);
      }
    },

    async downloadDoctorInvoice(consultation) {
      // Set downloading state for this specific consultation
      this.$set(consultation, "downloadingDoctor", true);

      try {
        const response = await axios.get(
          `/consultations/${this.encryptIt(
            consultation.id
          )}/download-doctor-invoice`,
          {
            responseType: "blob",
          }
        );
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Generate filename with doctor name and date
        const doctorName = consultation.doctor
          ? `${consultation.doctor.first_name}_${consultation.doctor.last_name}`.replace(
              /\s+/g,
              "_"
            )
          : "Unknown_Doctor";
        const date = new Date().toISOString().split("T")[0];
        const filename = `doctor_commission_invoice_${doctorName}_${date}.pdf`;

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        // Show success message
        this.$store.dispatch("errors/setSuccess", {
          message: "Doctor commission invoice downloaded successfully!",
        });
      } catch (error) {
        console.error("Error downloading doctor invoice:", error);

        if (error.response && error.response.status === 401) {
          this.$store.dispatch("errors/setError", {
            message: "Authentication required. Please login again.",
          });
          // Redirect to login
          this.$router.push("/login");
        } else if (error.response && error.response.status === 403) {
          this.$store.dispatch("errors/setError", {
            message:
              "Access denied. You don't have permission to download this doctor commission invoice.",
          });
        } else if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", {
            message:
              "Consultation not found. Please refresh the page and try again.",
          });
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              "Error downloading doctor commission invoice. Please try again.",
          });
        }
      } finally {
        // Remove downloading state
        this.$set(consultation, "downloadingDoctor", false);
      }
    },

    calculateCardHeight() {
      const viewportHeight = window.innerHeight;
      const headerHeight = 64; // App header
      const containerPadding = 48; // Container padding
      const filterCardHeight = 140; // First card (header/filters)
      const cardHeaderHeight = 60; // Second card header
      const paginationHeight = 60; // Pagination footer

      this.cardHeight =
        viewportHeight -
        headerHeight -
        containerPadding -
        filterCardHeight -
        cardHeaderHeight -
        paginationHeight;

      if (this.cardHeight < 400) {
        this.cardHeight = 400;
      }
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.consultation-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.consultation-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>